import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Heart, ShoppingBag, User, Menu, X, ChevronRight, Phone, Mail } from "lucide-react";
import { useStore } from "@/store/StoreContext";
import api from "@/api";
import { imgW, imgFallback } from "@/utils/img";

function MegaPanel({ item, onClose, onEnter }) {
  const mega = item.mega || {};
  const cols = (item.children || []).filter((c) => c.enabled !== false);
  return (
    <div data-testid="mega-menu-panel"
      className="absolute left-0 right-0 top-full z-50 bg-white border-t border-b border-[#EAEAEA] shadow-[0_40px_60px_-25px_rgba(18,18,18,0.25)] tq-mega-in"
      onMouseEnter={onEnter} onMouseLeave={onClose}>
      <div className="tq-container py-10 grid grid-cols-12 gap-10">
        <div className="col-span-3 pr-6 border-r border-[#FAFAFA]">
          <p className="tq-overline !text-[#A8821B]">{mega.heading || "Shop"}</p>
          {mega.subheading && <p className="font-serif text-[1.7rem] leading-snug mt-3 text-[#111111]">{mega.subheading}</p>}
        </div>
        <div className="col-span-6 grid grid-cols-3 gap-x-10">
          {cols.map((col) => (
            <div key={col.id} className="min-w-0">
              <Link to={col.url} onClick={onClose} data-testid={`mega-col-${col.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="block text-[11px] font-bold uppercase tracking-[0.22em] text-[#111111] pb-2.5 mb-4 border-b border-[#C59B27] hover:text-[#A8821B] transition-colors whitespace-nowrap overflow-hidden text-ellipsis">
                {col.label}
              </Link>
              <ul className="space-y-3">
                {(col.children || []).filter((g) => g.enabled !== false).map((g) => (
                  <li key={g.id}>
                    <Link to={g.url} onClick={onClose}
                      className="block text-[13px] text-[#666666] hover:text-[#111111] hover:translate-x-1 transition-all duration-200">
                      {g.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        {mega.image ? (
          <Link to={mega.promo_link || item.url} onClick={onClose} data-testid="mega-promo"
            className="col-span-3 relative group overflow-hidden block self-start">
            <img src={mega.image} alt={mega.promo_text || item.label} loading="lazy"
              className="w-full h-60 object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/60 to-transparent" />
            {mega.promo_text && (
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <p className="text-white font-serif text-xl leading-tight">{mega.promo_text}</p>
                <p className="text-[#C59B27] text-[10px] uppercase tracking-[0.25em] mt-1">Shop Now →</p>
              </div>
            )}
          </Link>
        ) : <div className="col-span-3" />}
      </div>
    </div>
  );
}

function MobileDrawer({ open, onClose, menu }) {
  const [expanded, setExpanded] = useState([]);
  const { user, settings } = useStore();
  if (!open) return null;
  const toggleExp = (id) => setExpanded((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  const renderItems = (items, depth = 0) => (
    <ul className={depth > 0 ? "pl-4 border-l border-[#EAEAEA] ml-1.5 mt-1" : ""}>
      {items.filter((i) => i.enabled !== false).map((item) => (
        <li key={item.id}>
          <div className="flex items-center justify-between gap-2">
            <Link to={item.url || "#"} onClick={onClose}
              className={`flex-1 py-3 block ${depth === 0 ? "font-serif text-[1.35rem] text-[#111111]" : "text-sm text-[#666666] py-2.5"}`}>
              {item.label}
              {item.tag && <span className="ml-2 text-[9px] font-sans font-bold uppercase tracking-wider text-[#C59B27] border border-[#C59B27] px-1.5 py-0.5 align-middle">{item.tag}</span>}
            </Link>
            {item.children?.some((c) => c.enabled !== false) && (
              <button onClick={() => toggleExp(item.id)}
                aria-label={`Expand ${item.label}`} data-testid={`mobile-expand-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="w-10 h-10 flex items-center justify-center shrink-0 text-[#C59B27]">
                <ChevronRight size={18} className={`transition-transform duration-300 ${expanded.includes(item.id) ? "rotate-90" : ""}`} />
              </button>
            )}
          </div>
          {expanded.includes(item.id) && item.children?.length > 0 && renderItems(item.children, depth + 1)}
        </li>
      ))}
    </ul>
  );
  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <div className="absolute inset-0 bg-[#111111]/60 backdrop-blur-[2px]" onClick={onClose} />
      <div className="absolute left-0 top-0 bottom-0 w-[86%] max-w-sm bg-[#FFFFFF] overflow-y-auto shadow-2xl" data-testid="mobile-menu-drawer">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#EAEAEA] sticky top-0 bg-[#FFFFFF] z-10">
          <div className="leading-none">
            <span className="font-serif text-2xl tracking-[0.12em]">TARISHQ</span>
            <p className="text-[8px] uppercase tracking-[0.5em] text-[#C59B27] mt-1">Fashion</p>
          </div>
          <button onClick={onClose} aria-label="Close menu" data-testid="mobile-menu-close" className="w-10 h-10 flex items-center justify-center"><X size={22} /></button>
        </div>
        <nav className="px-6 py-4">
          {renderItems(menu?.items || [])}
          <div className="mt-6 pt-6 border-t border-[#EAEAEA] space-y-4 text-sm">
            <Link to="/account" onClick={onClose} className="flex items-center gap-3 text-[#111111]"><User size={17} className="text-[#A8821B]" /> {user ? user.name : "Login / Register"}</Link>
            <Link to="/pages/track-order" onClick={onClose} className="flex items-center gap-3 text-[#111111]"><ShoppingBag size={17} className="text-[#A8821B]" /> Track Order</Link>
            <Link to="/wishlist" onClick={onClose} className="flex items-center gap-3 text-[#111111]"><Heart size={17} className="text-[#A8821B]" /> Wishlist</Link>
          </div>
          <div className="mt-6 pt-6 border-t border-[#EAEAEA] space-y-3 text-sm text-[#666666]">
            {settings?.store?.phone && <a href={`tel:${settings.store.phone.replace(/\s/g, "")}`} className="flex items-center gap-3"><Phone size={16} /> {settings.store.phone}</a>}
            {settings?.store?.email && <a href={`mailto:${settings.store.email}`} className="flex items-center gap-3 break-all"><Mail size={16} /> {settings.store.email}</a>}
          </div>
        </nav>
      </div>
    </div>
  );
}

function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ products: [], categories: [] });
  const navigate = useNavigate();
  const ref = useRef(null);
  useEffect(() => {
    if (open) setTimeout(() => ref.current?.focus(), 100);
    if (!open) { setQ(""); setResults({ products: [], categories: [] }); }
  }, [open]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults({ products: [], categories: [] }); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/search/suggest", { params: { q } }); setResults(data); } catch (e) { /* silent */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  if (!open) return null;
  const submit = (e) => { e.preventDefault(); if (q.trim()) { onClose(); navigate(`/search?q=${encodeURIComponent(q.trim())}`); } };
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-[#111111]/60 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute top-0 left-0 right-0 bg-white shadow-xl" data-testid="search-overlay">
        <form onSubmit={submit} className="tq-container py-6 flex items-center gap-4">
          <Search size={20} className="text-[#C59B27] shrink-0" />
          <input ref={ref} value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input"
            placeholder="Search heels, juttis, kolhapuris…" className="flex-1 text-lg font-serif outline-none placeholder:text-[#b9b2a6] bg-transparent" />
          <button type="button" onClick={onClose} aria-label="Close search" data-testid="search-close"><X size={22} /></button>
        </form>
        {(results.products.length > 0 || results.categories.length > 0) && (
          <div className="tq-container pb-8 grid sm:grid-cols-2 gap-6">
            <div>
              {results.categories.map((c) => (
                <Link key={c.slug} to={`/collections/${c.slug}`} onClick={onClose}
                  className="block py-2 text-xs uppercase tracking-[0.2em] text-[#C59B27] hover:text-[#111111]">
                  Category — {c.name}
                </Link>
              ))}
            </div>
            <div className="space-y-3">
              {results.products.map((p) => (
                <Link key={p.id} to={`/products/${p.slug}`} onClick={onClose} className="flex items-center gap-3 group">
                  <img src={imgW(p.images?.[0]?.url, 200)} alt={p.name} loading="lazy" onError={imgFallback} className="w-12 h-14 object-cover bg-[#FAFAFA]" />
                  <div>
                    <p className="text-sm font-medium group-hover:text-[#A8821B] transition-colors">{p.name}</p>
                    <p className="text-xs text-[#C59B27]">₹{p.price?.toLocaleString("en-IN")}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Header() {
  const { settings, menus, cartCount, user, setCartOpen } = useStore();
  const [megaOpen, setMegaOpen] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setMegaOpen(null); setDrawerOpen(false); setSearchOpen(false); }, [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const menu = menus.find((m) => m.handle === "main") || menus[0];
  const ann = settings?.announcement || {};
  const annList = (ann.items || []).filter((i) => i.enabled !== false && i.text);
  if (!annList.length && ann.text) annList.push({ text: ann.text, mobile_text: ann.mobile_text, link: ann.link });
  const openItem = (menu?.items || []).find((i) => i.id === megaOpen);

  return (
    <>
      {ann.enabled && annList.length > 0 && (
        <div className="tq-marquee overflow-hidden bg-[#111111] text-[#FFFFFF] text-[10px] sm:text-[11px] uppercase tracking-[0.25em] py-2" data-testid="announcement-bar">
          <div className="tq-marquee-track" style={{ "--tq-marquee-dur": `${ann.speed || 30}s` }}>
            {[0, 1].map((dup) => (
              <div key={dup} aria-hidden={dup === 1} className="flex items-center shrink-0">
                {annList.map((a, i) => (
                  <Link key={i} to={a.link || "#"} data-testid={dup === 0 ? `announcement-item-${i}` : undefined}
                    className="flex items-center px-6 hover:text-[#C59B27] transition-colors">
                    <span className="hidden sm:inline whitespace-nowrap">{a.text}</span>
                    <span className="sm:hidden whitespace-nowrap">{a.mobile_text || a.text}</span>
                    <span className="text-[#C59B27] pl-12" aria-hidden="true">✦</span>
                  </Link>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
      <header onMouseLeave={() => setMegaOpen(null)} className={`sticky top-0 z-40 relative bg-[#FFFFFF]/95 backdrop-blur-md border-b border-[#EAEAEA] transition-shadow ${scrolled ? "shadow-[0_4px_20px_-8px_rgba(18,18,18,0.15)]" : ""}`}>
        <div className="tq-container">
          <div className="flex items-center justify-between h-14 lg:h-16">
            <button className="lg:hidden p-2 -ml-2" onClick={() => setDrawerOpen(true)} aria-label="Open menu" data-testid="mobile-menu-btn">
              <Menu size={22} />
            </button>
            <Link to="/" data-testid="nav-brand-logo" className="flex flex-col items-start leading-none">
              {settings?.store?.logo ? (
                <img src={settings.store.logo} alt={settings.store.name} className="h-8 lg:h-10 object-contain" />
              ) : (
                <>
                  <span className="font-serif text-2xl lg:text-3xl tracking-[0.12em] text-[#111111]">TARISHQ</span>
                  <span className="text-[8px] uppercase tracking-[0.5em] text-[#C59B27] mt-0.5">Fashion</span>
                </>
              )}
            </Link>
            <nav className="hidden lg:flex items-center gap-9" data-testid="desktop-nav">
              {(menu?.items || []).filter((i) => i.enabled !== false).map((item) => (
                <div key={item.id} onMouseEnter={() => item.children?.some((c) => c.enabled !== false) ? setMegaOpen(item.id) : setMegaOpen(null)}>
                  <Link to={item.url || "#"} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    target={item.new_tab ? "_blank" : undefined}
                    className={`relative flex items-center gap-1.5 text-[13px] font-medium uppercase tracking-[0.15em] py-7 transition-colors border-b-2 after:content-[''] after:absolute after:bottom-5 after:left-0 after:w-0 after:h-[1px] after:bg-[#C59B27] after:transition-all after:duration-300 hover:after:w-full ${megaOpen === item.id ? "border-[#C59B27] text-[#A8821B]" : "border-transparent hover:text-[#A8821B]"}`}>
                    {item.label}
                    {item.tag && <span className="text-[8px] font-bold text-[#C59B27] border border-[#C59B27] px-1 py-0.5">{item.tag}</span>}
                  </Link>
                </div>
              ))}
            </nav>
            <div className="flex items-center gap-1 sm:gap-3">
              <button onClick={() => setSearchOpen(true)} aria-label="Search" data-testid="nav-search-btn"
                className="p-2.5 hover:text-[#A8821B] transition-colors"><Search size={20} /></button>
              <Link to="/account" aria-label="Account" data-testid="nav-account-btn"
                className="hidden sm:block p-2.5 hover:text-[#A8821B] transition-colors"><User size={20} /></Link>
              <Link to="/wishlist" aria-label="Wishlist" data-testid="nav-wishlist-btn"
                className="hidden sm:block p-2.5 hover:text-[#A8821B] transition-colors"><Heart size={20} /></Link>
              <button onClick={() => setCartOpen(true)} aria-label="Cart" data-testid="nav-cart-btn"
                className="relative p-2.5 hover:text-[#A8821B] transition-colors">
                <ShoppingBag size={20} />
                {cartCount > 0 && (
                  <span data-testid="cart-count-badge"
                    className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-[#C59B27] text-[#111111] text-[10px] font-bold flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
        {openItem && <MegaPanel item={openItem} onEnter={() => setMegaOpen(openItem.id)} onClose={() => setMegaOpen(null)} />}
      </header>
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#EAEAEA] grid grid-cols-5 py-1.5" data-testid="mobile-bottom-nav">
        {[
          { to: "/", label: "Home", testid: "bottom-nav-home", home: true },
          { to: "/collections/new-arrivals", icon: ShoppingBag, label: "Shop", testid: "bottom-nav-shop" },
          { to: null, icon: Search, label: "Search", testid: "bottom-nav-search", action: () => setSearchOpen(true) },
          { to: "/wishlist", icon: Heart, label: "Wishlist", testid: "bottom-nav-wishlist" },
          { to: "/account", icon: User, label: "Account", testid: "bottom-nav-account" },
        ].map((item) => item.action ? (
          <button key={item.label} onClick={item.action} data-testid={item.testid}
            className="flex flex-col items-center gap-0.5 py-1.5 text-[#666666]">
            <item.icon size={19} /><span className="text-[9px] uppercase tracking-wider">{item.label}</span>
          </button>
        ) : (
          <Link key={item.label} to={item.to} data-testid={item.testid}
            className="flex flex-col items-center gap-0.5 py-1.5 text-[#666666]">
            {item.home
              ? <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12l9-8 9 8M5 10v10h5v-6h4v6h5V10"/></svg>
              : <item.icon size={19} />}
            <span className="text-[9px] uppercase tracking-wider">{item.label}</span>
          </Link>
        ))}
      </nav>
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} menu={menu} />
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
